import { api } from "../../lib/api";

export type Customer = {
  id: number;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  note: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CustomerBalance = {
  balanceCents: number;
  pendingSalesCount: number;
  paidCents: number;
  totalChargedCents: number;
};

export type AccountMovement = {
  id: number;
  customerId: number;
  type: "entrega";
  amountCents: number;
  paymentMethodId: number | null;
  occurredAt: string;
  operatorId: number;
  note: string | null;
};

export type AccountSettlementLine = {
  paymentMethodId: number;
  /** Canonical amount paid by the customer. receivedCents remains API-compatible temporarily. */
  customerPaidCents: number;
  receivedCents?: number;
  netReceivedCents?: number;
  skipNetReceived?: boolean;
  discountCents: number;
  discountSource: "none" | "configured" | "manual";
  discountReason?: string | null;
  allocations: Array<{
    saleId: number;
    customerPaidAppliedCents: number;
    /** Transitional alias accepted by the API while older clients exist. */
    cashAppliedCents?: number;
    discountCents: number;
  }>;
};

export type AccountCollection = {
  id: string;
  collectionId: string | null;
  occurredAt: string;
  operator: { id: number; name: string };
  note: string | null;
  status: "active" | "reverted" | "mixed";
  customerPaidCents: number;
  discountCents: number;
  debtCancelledCents: number;
  /** Sum of line nets that are known individually. Batch nets stay at the batch level. */
  netReceivedCents: number;
  commissionCents: number;
  pendingNetCents: number;
  pendingSettlementCents: number;
  lines: Array<{
    id: number;
    collectionLineNumber: number;
    paymentMethodId: number;
    paymentMethod: {
      name: string;
      color: string | null;
      icon: string | null;
      requiresNetReceived: boolean;
      settlesInBatch: boolean;
    };
    customerPaidCents: number;
    discountPctSnapshot: number;
    discountCents: number;
    debtCancelledCents: number;
    netReceivedCents: number | null;
    commissionCents: number;
    discountSource: "none" | "configured" | "manual" | string;
    discountReason: string | null;
    status: "active" | "reverted" | string;
    revertedAt: string | null;
    reversalReason: string | null;
    batchSettlement: { id: number; status: string } | null;
    settlesInBatch: boolean;
    allocations: Array<{
      saleId: number;
      customerPaidAppliedCents: number;
      discountCents: number;
      debtCancelledCents: number;
    }>;
  }>;
};

export type CustomerAccount = {
  customer: Customer;
  balance: CustomerBalance;
  pendingSales: Array<{
    saleId: number;
    createdAt: string;
    itemsCount: number;
    originalCreditCents: number;
    historicalAppliedCents: number;
    allocatedCashCents: number;
    discountCents: number;
    totalAppliedCents: number;
    pendingCents: number;
    allocationSource: "historical_fifo_projection";
  }>;
  historicalAllocation: {
    unallocatedCustomerCreditCents: number;
  };
  /** New real collections, grouped by the one operator action. Legacy movements stay separate. */
  collections: AccountCollection[];
  sales: Array<{
    id: number;
    createdAt: string;
    status: "active" | "voided";
    voidedAt: string | null;
    voidReason: string | null;
    items: Array<{
      id: number;
      productName: string;
      qty: number;
      lineSubtotalCents: number;
      note: string | null;
    }>;
    totalChargedCents: number;
    contributedToBalance: number;
    historicalAppliedCents: number;
    remainingCents: number;
  }>;
  movements: AccountMovement[];
};

export type CustomerListEntry = Customer & {
  balance?: CustomerBalance;
};

export const customersApi = {
  list: (params: { q?: string; includeInactive?: boolean; withBalance?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set("q", params.q);
    if (params.includeInactive) q.set("includeInactive", "true");
    if (params.withBalance) q.set("withBalance", "true");
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api<CustomerListEntry[]>(`/api/customers${qs ? `?${qs}` : ""}`);
  },
  create: (body: {
    firstName: string;
    lastName?: string | null;
    phone?: string | null;
    email?: string | null;
    note?: string | null;
  }) => api<Customer>("/api/customers", { method: "POST", body }),
  patch: (id: number, body: Partial<Omit<Customer, "id" | "createdAt" | "updatedAt">>) =>
    api<Customer>(`/api/customers/${id}`, { method: "PATCH", body }),
  account: (id: number) => api<CustomerAccount>(`/api/customers/${id}/account`),
};

export const accountMovementsApi = {
  list: (customerId: number, params: { from?: string; to?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api<AccountMovement[]>(
      `/api/customers/${customerId}/account-movements${qs ? `?${qs}` : ""}`
    );
  },
};

export const accountSettlementsApi = {
  create: (customerId: number, body: { note?: string | null; lines: AccountSettlementLine[]; moneyConfirmationToken?: string }) =>
    api(`/api/customers/${customerId}/account-settlements`, { method: "POST", body }),
};
